import React, { useState } from 'react';
import {
  Alert, Box,
  TextField,
  Grid,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import MobileSheet from './common/MobileSheet';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useHeaderConfig } from './SidebarAndHeaderBar'; // adjust path if needed

const API = process.env.REACT_APP_API_BASE_URL;

export default function VendorWizard() {
  const navigate = useNavigate();
  const { setHeaderConfig } = useHeaderConfig();

  const [form, setForm] = useState({
    display_name: '',
    official_name: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    contact_channel: '',
    lead_time: '',
    shipping_time: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  React.useEffect(() => {
    setHeaderConfig({
      title: 'Create Vendor',
      showSearch: false,
      actions: [],
    });
    return () => {
      setHeaderConfig({ title: '', showSearch: true, actions: [] });
    };
  }, [setHeaderConfig]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      // 🔴 M6: Komma zu Punkt. Auf deutschen Geraeten traegt die Zifferntastatur
      // ein Komma; `Number('1,5')` waere `NaN` und ginge still als `null` hinaus
      // (JSON). Hausform: components/po/SendungDialog.jsx:268-277.
      // 🔴 M6-Nachbesserung (Regel-16-Tor W4): dazu der `Number.isFinite`-Waechter
      // aus admin/v2/NotificationTriggers.jsx:59-65. Ohne ihn ist `NaN` nur
      // zufaellig `null` — `Number('3x')` faellt jetzt an, weil das Feld seit M6
      // ein TEXTfeld ist (`type="number"` schluckte Buchstaben). Ein unlesbarer
      // Wert haelt den Submit an, statt die Lieferzeit kommentarlos leer zu lassen.
      const alsZahl = (v) => {
        if (v === '' || v == null) return null
        const n = Number(String(v).replace(',', '.'))
        return Number.isFinite(n) ? n : null
      };
      // Leer ist erlaubt (das Feld ist freiwillig) — unlesbar, nicht ganzzahlig
      // oder ausserhalb des Bereichs nicht.
      // 🔴 M6-Nachbesserung 2 (Regel-16-Tor W6): `unlesbar` prueft nur die
      // LESBARKEIT, die Meldung verspricht aber eine GANZE Zahl. `3,5` ist finit
      // und ging als `3.5` hinaus — der Server lehnte ihn danach mit DEUTSCHEM
      // Text ab (backend/lieferant_pruefung.py:219-220, `_ganzzahl(0, 365, "Tage")`),
      // derselbe Bedienfehler erzeugte also zwei Meldungen, eine in der falschen
      // Sprache. `Number.isInteger` macht den englischen Satz wahr und haelt den
      // Submit an, BEVOR der Server gefragt wird. Nur diese zwei Felder: die
      // Prozentspalte desselben Backends erlaubt Nachkommastellen (`_prozent`).
      // 🔴 M6-Nachbesserung 4 (Regel-16-Tor W5): die GRENZE gehoert dazu.
      // Dieselbe Serverregel ist `_ganzzahl(0, 365, "Tage")`
      // (backend/lieferant_pruefung.py:219-220) — sie lehnt `400` ab, und zwar
      // mit demselben deutschen Satz, den die Nachbesserung 2 gerade abgeschafft
      // hat. Die Grenze steht jetzt hier, und die Meldung NENNT sie.
      const tageUngueltig = (v) => {
        if (v === '' || v == null) return false
        const n = alsZahl(v)
        return n === null || !Number.isInteger(n) || n < 0 || n > 365
      }
      if (tageUngueltig(form.lead_time)) {
        setError('Lead time must be a whole number of days from 0 to 365.')
        return
      }
      if (tageUngueltig(form.shipping_time)) {
        setError('Shipping time must be a whole number of days from 0 to 365.')
        return
      }
      const res = await axios.post(`${API}/api/vendors`, {
        ...form,
        lead_time: alsZahl(form.lead_time),
        shipping_time: alsZahl(form.shipping_time),
      });
      const id = res.data.id;
      navigate(`/vendors/${id}#addresses`); // Regel-16-Tor Stufe b, W3: straight to the Addresses section
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };
  const formId = 'vendor-create-form';
  const handleClose = () => navigate('/vendors');

  return (
    <MobileSheet open onClose={handleClose} maxWidth="sm">
      <DialogTitle>Create Vendor</DialogTitle>
      <DialogContent>
        <Box component="form" id={formId} onSubmit={onSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                label="Display name (short)"
                name="display_name"
                value={form.display_name}
                onChange={onChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                required
                fullWidth
                label="Official brand name"
                name="official_name"
                value={form.official_name}
                onChange={onChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Name"
                name="contact_name"
                value={form.contact_name}
                onChange={onChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Email"
                name="contact_email"
                value={form.contact_email}
                onChange={onChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Phone" name="phone" value={form.phone} onChange={onChange} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Contact Channel"
                name="contact_channel"
                value={form.contact_channel}
                onChange={onChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {/* 🔴 M6: `type="number"` entfernt — es hat den `inputMode` daneben
                  ueberschrieben. Ganze Tage → `numeric`. Hausform:
                  components/po/SendungDialog.jsx:637-639.
                  🔴 M6-Nachbesserung (Regel-16-Tor W5): `min: 0` ist mitgefallen.
                  In einem TEXTfeld ist `min` wirkungslos, sah aber wie eine Pruefung
                  aus — und es war nur ein Bruchstueck der echten Spanne: das Backend
                  fuehrt `_ganzzahl(0, 365, "Tage")` (`backend/lieferant_pruefung.py:219`)
                  und meldet einen Verstoss SICHTBAR am Feld.
                  🔴 M6-Nachbesserung 4 (Regel-16-Tor W5): dieselbe Spanne prueft
                  jetzt auch der CLIENT (`tageUngueltig`, oben in `onSubmit`) —
                  sonst kaeme fuer `400` der deutsche Satz des Servers durch, also
                  genau die Lage, die Nachbesserung 2 abgeschafft hat. */}
              <TextField
                fullWidth
                label="Lead time (days)"
                name="lead_time"
                value={form.lead_time}
                onChange={onChange}
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {/* 🔴 M6: wie das Feld daneben — `inputMode="numeric"` statt
                  `type="number"`. 🔴 M6-Nachbesserung (W5): `min: 0` mitgefallen,
                  wie beim Lead time — in einem Textfeld wirkungslos. */}
              <TextField
                fullWidth
                label="Shipping time (days)"
                name="shipping_time"
                value={form.shipping_time}
                onChange={onChange}
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/* Stufe b (2026-09-26): no free-text address at creation; rows go to
                  vendor_addresses through the "Addresses" section of the detail.
                  Regel-16-Tor W3: below the last field, as the info notice the
                  section itself uses, and the submit lands on `#addresses`. */}
              <Alert severity="info">
                Addresses (headquarters, warehouse, pickup) are added after creating the vendor:
                you land in the Addresses section of the vendor page.
              </Alert>
            </Grid>
            {error && (
              <Grid size={{ xs: 12 }}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" form={formId} variant="contained" disabled={saving}>
          {saving ? 'Creating…' : 'Create vendor'}
        </Button>
      </DialogActions>
    </MobileSheet>
  );
}
